// utils/pilot-picker.ts — Picker d'utilisateurs (dropdown ancré, recherche, multi-select).
//
// Historique : factorisait déjà le picker dupliqué À L'IDENTIQUE entre
// page-projects (liste) et page-project/index (édition) — juin 2026, dette #6a-1.
//
// L'audit front du 2026-09-10 en a trouvé trois copies de plus, restées en
// dehors : deux dans `page-programs.ts` (le même picker écrit deux fois dans le
// MÊME fichier, à 400 lignes d'intervalle) et une dans
// `page-admin-structures.ts`, que son propre commentaire décrivait comme une
// « copie fidèle du picker des pilotes de programme ». Elles ne différaient que
// par le préfixe des classes CSS et les clés i18n — d'où les deux paramètres
// `prefix` et `labels`. Les classes gardent leur nom d'origine sur chaque page
// (rien d'externe ne les cible : le style est inline, les e2e ne les utilisent
// pas), pour que le diff reste lisible côté DOM.
//
// Mute `selected` en place. Réconcilie les variantes via callbacks optionnels :
//   - onChange : appelé après chaque (dé)sélection (marque le formulaire dirty).
//   - onClose  : appelé à la fermeture (rafraîchit les puces).
//
// `esc`, `registerPageDisposer` sont des globals ambiants (core-globals.d.ts).

export interface PilotUser {
  id: number;
  name?: string;
  trigram?: string | null;
}

/** Les trois libellés du dropdown, DÉJÀ traduits par l'appelant. */
export interface PilotPickerLabels {
  /** Placeholder du champ de recherche. */
  search: string;
  /** Ligne affichée quand la recherche ne ramène rien. */
  empty: string;
  /** Libellé du bouton de validation. */
  add: string;
}

export interface PilotPickerOpts {
  anchor: HTMLElement;
  users: PilotUser[];
  /** Set muté en place au fil des cases cochées/décochées. */
  selected: Set<number>;
  /** Préfixe des classes CSS : `pm-pilot`, `pgm-pilot`, `st-resp`. */
  prefix: string;
  labels: PilotPickerLabels;
  /** Après chaque (dé)sélection — ex. marquer le formulaire « dirty ». */
  onChange?: () => void;
  /** À la fermeture du dropdown — ex. rafraîchir les puces sélectionnées. */
  onClose?: () => void;
}

/**
 * Picker de pilotes de PROJET : classes et libellés sont les mêmes pour la
 * liste (`page-projects`) et l'édition (`page-project/index`), seul le contexte
 * change. Les libellés sont lus à l'ouverture et non figés en constante : `t`
 * dépend de la langue courante, qui change au runtime.
 */
export function openProjectPilotPicker(o: Omit<PilotPickerOpts, 'prefix' | 'labels'>): void {
  openPilotPicker({
    ...o,
    prefix: 'pm-pilot',
    labels: {
      search: t('pageProjects.picker.search'),
      empty: t('pageProjects.picker.empty'),
      add: t('pageProjects.picker.btnAdd'),
    },
  });
}

const WIDTH = 300;
const MAX_HEIGHT = 380;
const MARGIN = 8;

/** Coin haut-gauche du dropdown : sous l'ancre, aligné à droite, rabattu si ça déborde. */
export function pickerPosition(
  rect: { top: number; bottom: number; left: number; right: number },
  viewport: { width: number; height: number },
): { top: number; left: number } {
  let left = rect.right - WIDTH;
  if (left < MARGIN) left = rect.left;
  if (left + WIDTH > viewport.width - MARGIN) left = viewport.width - WIDTH - MARGIN;
  let top = rect.bottom + 4;
  if (top + MAX_HEIGHT > viewport.height - MARGIN)
    top = Math.max(MARGIN, rect.top - MAX_HEIGHT - 4);
  return { top, left };
}

/**
 * Ouvre, ancré sous `o.anchor`, un dropdown de sélection multiple d'utilisateurs
 * (recherche nom/trigramme, cases à cocher). Re-clic sur l'ancre = ferme
 * (toggle, géré ici). Auto-nettoyé au changement de page.
 */
export function openPilotPicker(o: PilotPickerOpts): void {
  const { anchor, users, selected, prefix, labels } = o;
  // Toggle : un picker déjà ouvert se referme (comportement d'origine).
  const existing = document.querySelector(`.${prefix}-picker`);
  if (existing) {
    existing.remove();
    return;
  }
  const rect = anchor.getBoundingClientRect();
  const dd = document.createElement('div');
  dd.className = `${prefix}-picker`;
  dd.style.cssText = [
    'position:fixed',
    'z-index:1000',
    'background:var(--bg2)',
    'border:1px solid var(--border2)',
    'border-radius:var(--radius)',
    'box-shadow:0 8px 24px rgba(0,0,0,.15)',
    `width:${WIDTH}px`,
    `max-height:${MAX_HEIGHT}px`,
    'display:flex',
    'flex-direction:column',
    'overflow:hidden',
  ].join(';');
  const renderListHtml = (filter: string): string => {
    const lower = (filter || '').toLowerCase();
    const matched = users.filter(
      (u) =>
        !lower ||
        (u.name || '').toLowerCase().includes(lower) ||
        (u.trigram || '').toLowerCase().includes(lower),
    );
    if (!matched.length)
      return `<div style="color:var(--text3);font-size:11px;font-style:italic;padding:8px">${esc(labels.empty)}</div>`;
    return matched
      .map(
        (u) => `
        <label class="${esc(prefix)}-row" style="display:flex;align-items:center;gap:6px;font-size:12px;cursor:pointer;padding:5px 10px;border-bottom:0.5px solid var(--border);${selected.has(u.id) ? 'background:var(--accent-bg)' : ''}">
          <input type="checkbox" class="${esc(prefix)}-chk" value="${u.id}" ${selected.has(u.id) ? 'checked' : ''}>
          <span style="font-family:var(--font-mono);font-size:11px;color:var(--text3);min-width:36px">${esc(u.trigram || '')}</span>
          <span>${esc(u.name || '')}</span>
        </label>`,
      )
      .join('');
  };

  dd.innerHTML = `
    <div style="padding:8px;border-bottom:1px solid var(--border)">
      <input type="text" class="${esc(prefix)}-pick-search" placeholder="${esc(labels.search)}"
        autocomplete="off"
        style="width:100%;font-size:12px;padding:6px 10px;border:1px solid var(--border2);border-radius:var(--radius)">
    </div>
    <div class="${esc(prefix)}-pick-list" style="flex:1;overflow-y:auto">${renderListHtml('')}</div>
    <div style="padding:8px 10px;border-top:1px solid var(--border);display:flex;justify-content:flex-end">
      <button type="button" class="${esc(prefix)}-pick-add btn btn-primary btn-sm">${esc(labels.add)}</button>
    </div>`;
  document.body.appendChild(dd);

  const pos = pickerPosition(rect, { width: window.innerWidth, height: window.innerHeight });
  dd.style.top = pos.top + 'px';
  dd.style.left = pos.left + 'px';

  const listEl = dd.querySelector<HTMLElement>(`.${prefix}-pick-list`);
  const searchEl = dd.querySelector<HTMLInputElement>(`.${prefix}-pick-search`);
  if (!listEl || !searchEl) return;
  searchEl.addEventListener('input', (e: Event) => {
    const target = e.target as HTMLInputElement | null;
    listEl.innerHTML = renderListHtml(target?.value || '');
  });
  listEl.addEventListener('change', (e: Event) => {
    const target = e.target as HTMLInputElement | null;
    if (!target || !target.classList.contains(`${prefix}-chk`)) return;
    const id = parseInt(target.value);
    if (target.checked) selected.add(id);
    else selected.delete(id);
    o.onChange?.();
    const row = target.closest<HTMLElement>(`.${prefix}-row`);
    if (row) row.style.background = target.checked ? 'var(--accent-bg)' : '';
  });
  setTimeout(() => searchEl.focus(), 0);

  const close = (): void => {
    document.removeEventListener('mousedown', onOutside, true);
    document.removeEventListener('keydown', onEsc, true);
    dd.remove();
    o.onClose?.();
  };
  const onOutside = (e: MouseEvent): void => {
    const tgt = e.target as Node | null;
    if (!tgt) return;
    if (dd.contains(tgt) || tgt === anchor || anchor.contains(tgt)) return;
    close();
  };
  const onEsc = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      // Sans ça, Escape fermerait AUSSI la modale sous le picker.
      e.stopPropagation();
      close();
    }
  };
  dd.querySelector<HTMLButtonElement>(`.${prefix}-pick-add`)?.addEventListener(
    'click',
    (e: Event) => {
      e.stopPropagation();
      close();
    },
  );
  document.addEventListener('mousedown', onOutside, true);
  document.addEventListener('keydown', onEsc, true);
  registerPageDisposer(() => {
    try {
      close();
    } catch {
      /* déjà fermé */
    }
  });
}
